import apiClient from './apiService';

// Audit universe — GRC persona (BRs AUV-40…70, AUA-10 in
// docs/audit-planning-memorandum.html). Backend: backend/routes/adt_universe_routes.py.

const BASE = '/api/v1/adt/universes';

export type UniverseWorkflowState = 'draft' | 'submitted' | 'approved' | 'rejected';

export interface UniverseListItem {
  id: number;
  universe_code: string;
  universe_name: string;
  category: string;
  objective?: string | null;
  owner_user_id?: number | null;
  owner_name?: string | null;
  workflow_state: UniverseWorkflowState;
  is_active: number;
  is_approved: number;
  unit_count: number;
  agency_names: string;
}

export interface UniverseUnit {
  id?: number;
  unit_name: string;
  unit_type: string;
  description?: string | null;
  recommended_program_id?: number | null;
  recommended_program_name?: string | null;
}

export interface UniverseMandate {
  id: number;
  agency_id: number;
  agency_name: string;
  agency_kind: string;
  fiscal_year: number;
  plan_due_date?: string | null;
  notes?: string | null;
}

export interface UniverseDetail extends UniverseListItem {
  description?: string | null;
  units: UniverseUnit[];
  mandates: UniverseMandate[];
}

export interface UniverseOptions {
  categories: string[];
  unit_types: string[];
  agencies: { id: number; name: string; agency_kind: string }[];
  programs: { id: number; name: string }[];
  users: { id: number; name: string }[];
  current_fiscal_year: number;
  fiscal_years: { value: number; label: string; start: string; end: string }[];
}

export interface UniverseInput {
  universe_name: string;
  category: string;
  objective?: string | null;
  description?: string | null;
  owner_user_id?: number | null;
}

export interface AgencyUniverseRow {
  fiscal_year: number;
  fiscal_year_label: string;
  plan_due_date?: string | null;
  universe_id: number;
  universe_code: string;
  universe_name: string;
  category: string;
  workflow_state: UniverseWorkflowState;
  unit_count: number;
}

export const auditUniverseService = {
  /** Universes mandated to one agency, newest year first (agency definition → Universes tab). */
  async byAgency(agencyId: number): Promise<AgencyUniverseRow[]> {
    return (await apiClient.get(`${BASE}/by-agency/${agencyId}`)).data;
  },
  async options(): Promise<UniverseOptions> {
    return (await apiClient.get(`${BASE}/options`)).data;
  },
  async list(): Promise<UniverseListItem[]> {
    return (await apiClient.get(BASE)).data;
  },
  async get(id: number): Promise<UniverseDetail> {
    return (await apiClient.get(`${BASE}/${id}`)).data;
  },
  async create(body: UniverseInput): Promise<{ id: number; universe_code: string }> {
    return (await apiClient.post(BASE, body)).data;
  },
  async update(id: number, body: UniverseInput): Promise<void> {
    await apiClient.put(`${BASE}/${id}`, body);
  },
  async setStatus(id: number, is_active: 0 | 1): Promise<void> {
    await apiClient.patch(`${BASE}/${id}/status`, { is_active });
  },
  async saveUnits(id: number, units: UniverseUnit[]): Promise<void> {
    await apiClient.put(`${BASE}/${id}/units`, {
      units: units.map(u => ({
        id: u.id ?? null,
        unit_name: u.unit_name,
        unit_type: u.unit_type,
        description: u.description ?? null,
        recommended_program_id: u.recommended_program_id ?? null,
      })),
    });
  },
  async saveMandates(
    id: number, fiscal_year: number,
    mandates: { agency_id: number; plan_due_date?: string | null; notes?: string | null }[],
  ): Promise<void> {
    await apiClient.put(`${BASE}/${id}/agencies`, { fiscal_year, mandates });
  },
};

export default auditUniverseService;
